/* eslint-disable no-underscore-dangle */
/* eslint-disable prefer-destructuring */
import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { UntypedFormBuilder, UntypedFormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { FlexLayoutModule } from '@angular/flex-layout';
import { from, Observable, of } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';

import { Logger } from '@sondermind/datadog-logging';
import { PatientService } from '@sondermind/data-access/patient';
import { SondermindEvent, SondermindEvents } from '@sondermind/analytics';
import {
  ClientReferralHttpService,
  ClientReferralMemberService,
  IProviderInvitedReferralRequestBody
} from '@sondermind/data-access/provider-facing/client-referrals';
import { FEATURE_FLAG_SERVICE, FeatureFlagKey, IFeatureFlagService } from '@sondermind/data-access/feature-flags';
import { SegmentService } from '@sondermind/segment';
import { BreakpointsService } from '@sondermind/utilities/services';
import { IrisButtonContainerAlignments, IrisButtonModule, IrisButtonTypes } from '@sondermind/iris/button';
import { IrisFormFieldModule } from '@sondermind/iris/form-field';
import { IrisRadioGroupModule, RadioOption } from '@sondermind/iris/radio-group';
import { IrisTextInputModule } from '@sondermind/iris/text-input';
import { ServerResponse } from '@sondermind/utilities/models-http';
import { Patient } from '@sondermind/utilities/models-patient';
import { ProviderUser } from '@sondermind/utilities/models-user-provider';
import { SharedMasks } from '@sondermind/utilities/text-masks';
import { AtLeastOneValidator } from '@sondermind/utilities/tools/validators/at-least-one.validator';
import { EmailValidator } from '@sondermind/utilities/tools/validators/email.validator';
import { PhoneValidator } from '@sondermind/utilities/tools/validators/phone.validator';

@Component({
  selector: 'calendar-embedded-client-creator',
  templateUrl: './embedded-client-creator.component.html',
  styleUrls: ['./embedded-client-creator.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FlexLayoutModule,
    IrisFormFieldModule,
    IrisTextInputModule,
    IrisRadioGroupModule,
    IrisButtonModule
  ]
})
export class EmbeddedClientCreatorComponent implements OnInit, OnDestroy {
  @Input() user: ProviderUser = new ProviderUser();
  @Input() cancelable: boolean = true;

  saving = false;
  private defaultFormValue: any = null;

  form: UntypedFormGroup = null;

  @Output() patientCreated = new EventEmitter<Patient>();
  @Output() canceled = new EventEmitter<boolean>();

  irisButtonTypes = IrisButtonTypes;
  irisButtonContainerAlignments = IrisButtonContainerAlignments;

  intakeRequiredOptions: Array<RadioOption<boolean>> = [
    { name: 'Yes', value: true },
    { name: 'No', value: false }
  ];

  isMobile$ = this._breakpoint.isMobile$;

  phoneNumberRequired = false;
  readonly phoneMask = SharedMasks['phoneMask'].ngxMask;

  private destroyed$ = new EventEmitter<void>();

  constructor(
    private _patientService: PatientService,
    private formBuilder: UntypedFormBuilder,
    private snackBar: MatSnackBar,
    private _breakpoint: BreakpointsService,
    private clientReferralService: ClientReferralHttpService,
    private clientReferralMemberService: ClientReferralMemberService,
    private segmentService: SegmentService<SondermindEvents>,
    private logger: Logger,
    @Inject(FEATURE_FLAG_SERVICE) private featureFlagService: IFeatureFlagService
  ) { }

  handleSave(event: any): void {
    if (this.form.invalid) { return }

    this.saving = true;

    this.createProviderInvitedReferral$().subscribe({
      next: (resp) => {
        this.trackSingleInviteEvent({ referral: resp.referralData, success: true });
        this.success(resp.patientResponse);
      },
      error: (err) => {
        this.trackSingleInviteEvent({ success: false });
        this.error(err);
      }
    });
  }

  private createProviderInvitedReferral$(): Observable<{
    patientResponse: ServerResponse<Patient, any>;
    referralData: { id: number; clientReferralMemberIds: number[] };
  }> {
    const data: IProviderInvitedReferralRequestBody = {
      first_name: this.form.value.firstName,
      last_name: this.form.value.lastName,
      email: this.form.value.email,
      intake_required: this.form.value.intakeRequired,
      // Omit phone_number entirely when absent: the backend input type is optional but not nilable,
      // so an explicit null fails request-body parsing.
      ...(this.form.value.phoneNumber ? { phone_number: this.form.value.phoneNumber } : {}),
      invite_type: 'single'
    };

    return this.clientReferralService.createProviderInvitedReferral$(data).pipe(
      switchMap((referralResp) => {
        const referralMemberId = referralResp.data.clientReferralMemberIds[0];
        return this.clientReferralMemberService.getMember$(referralMemberId).pipe(
          switchMap((memberResp) => this._patientService.load(memberResp.data.patientId).pipe(
            switchMap((patientResp) => of({ patientResponse: patientResp, referralData: referralResp.data }))
          ))
        );
      })
    );
  }

  private trackSingleInviteEvent(
    outcome: { referral?: { id: number; clientReferralMemberIds: number[] }; success: boolean }
  ): void {
    if (!this.user.personaProviderId) { return; }

    from(this.segmentService.track<SondermindEvent.ProviderInvitedClients>(
      SondermindEvent.ProviderInvitedClients,
      {
        personaProviderId: this.user.personaProviderId,
        inviteType: 'single',
        clientCount: outcome.success ? 1 : 0,
        referralIds: outcome.referral ? [outcome.referral.id] : [],
        referralMemberIds: outcome.referral?.clientReferralMemberIds ?? [],
        intakeRequired: this.form.get('intakeRequired')?.value ?? true,
        success: outcome.success,
        failureCount: outcome.success ? 0 : 1,
      }
    )).subscribe({
      error: (err) => this.logger.warn('Segment tracking failed for Provider Invited Clients', { err })
    });
  }

  success(response: ServerResponse<Patient, any>): void {
    this.patientCreated.emit(response.value);
    this.reset();
    this.saving = false;
  }

  error(error: any): void {
    this.snackBar.open('Sorry something went wrong', 'Dismiss');
    this.logger.error('EmbeddedClientCreator save failed', { err: error });
    this.saving = false;
  }

  handleCancel(event: any): void {
    this.reset();
    this.canceled.emit(true);
  }

  ngOnInit(): void {
    this.featureFlagService.isEnabled$(FeatureFlagKey.CLIENT_INVITE_PHONE_NUMBER_REQUIRED)
      .pipe(take(1))
      .subscribe((enabled) => {
        this.phoneNumberRequired = enabled;
        this.form = this.buildForm();

        // save off default for when we reset
        this.defaultFormValue = this.form.value;
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
  }

  get valid(): boolean {
    return this.form.valid;
  }

  /**
   * Get form-level error message if present
   * Used to display form-level validation errors (e.g., from AtLeastOneValidator)
   */
  get formLevelError(): string | null {
    if (!this.form?.errors || !this.form.dirty) {
      return null;
    }

    const errors = this.form.errors;
    // The AtLeastOneValidator creates an error with key 'base'
    if (errors['base']) {
      const baseError = errors['base'];
      // If it's a string, return it directly; otherwise return the default message
      return typeof baseError === 'string' ? baseError : 'A first or last name is required!';
    }

    return null;
  }

  /**
   * Get the phone number field error message, if present.
   * Used to display the field-level validation error (e.g., required, invalid format).
   */
  get phoneNumberError(): string | null {
    const control = this.form?.get('phoneNumber');
    if (!control || !(control.touched || control.dirty)) {
      return null;
    }

    if (control.errors?.['required']) {
      return 'Phone number is required';
    }

    if (control.errors?.['phoneNumberFormat']) {
      return control.errors['phoneNumberFormat'];
    }

    return null;
  }

  /**
   * Snake_case names of the form fields that currently hold a value. Sent as
   * `completedFields` on the Provider Invited Clients Cancelled event.
   */
  get completedFields(): string[] {
    if (!this.form) { return []; }

    const fieldNames: Record<string, string> = {
      firstName: 'first_name',
      lastName: 'last_name',
      email: 'email',
      phoneNumber: 'phone_number',
      intakeRequired: 'intake_required'
    };

    return Object.entries(fieldNames)
      .filter(([controlName]) => {
        const value = this.form.get(controlName)?.value;
        return value !== null && value !== undefined && value !== '';
      })
      .map(([, eventName]) => eventName);
  }

  reset(): void {
    this.form.reset(this.defaultFormValue);
  }

  /**
   * Build form according to the invite client variant.
   */
  private buildForm(): UntypedFormGroup {
    const compose = Validators.compose;
    const { required } = Validators;

    const phoneValidators = this.phoneNumberRequired
      ? compose([PhoneValidator.format, required])
      : PhoneValidator.format;

    const formControls = {
      firstName: [null, required],
      lastName: [null, required],
      email: [null, compose([EmailValidator.format, required])],
      intakeRequired: [true, required],
      phoneNumber: [null, phoneValidators],
    };

    const validators = [
      AtLeastOneValidator.of(['firstName', 'lastName'], { base: 'A first or last name is required!' })
    ];

    const formGroup: UntypedFormGroup = this.formBuilder.group(
      formControls,
      {
        validator: compose(
          validators
        )
      }
    );

    return formGroup;
  }
}
